import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Building2, Users, Mail, Trash2, Copy, Send, Plus, ShieldCheck,
  Settings as SettingsIcon, Webhook, FolderOpen, MessageSquare, Crown,
  DollarSign, BarChart3, UserMinus, UserCheck, AlertTriangle, LineChart, Archive, Download, ShieldAlert, CheckCircle2, Lock
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { formatDate, formatDateTime } from "@/lib/format";
import { Link } from "react-router-dom";

import { useLocation } from "react-router-dom";
import MeetingTemplatesTab from "@/components/MeetingTemplatesTab";

// iter51 — Doc Library defaults (used as seed when an org has no custom list yet)
const DEFAULT_PILLARS = ["Quality", "Food Safety / HACCP", "Maintenance", "Sanitation",
  "HR / People", "Environmental / Sustainability", "Safety", "Operations", "Other"];
const DEFAULT_CODE_PREFIXES = [
  { code: "SOP", label: "SOP (Standard Operating Procedure)" },
  { code: "WI", label: "WI (Work Instruction)" },
  { code: "POL", label: "POL (Policy)" },
  { code: "FRM", label: "FRM (Form)" },
  { code: "RCD", label: "RCD (Record)" },
  { code: "MAN", label: "MAN (Manual)" },
];

export default function Admin() {
  const { user } = useAuth();
  const loc = useLocation();
  const role = user?.org_role;
  const isController = role === "controller";
  const isAdmin = role === "admin" || isController;
  // Split views: /app/admin shows admin-oriented tabs, /app/controller shows controller-only tabs.
  // Master (identified via user?.is_master or role=controller) can hit either route.
  const isControllerRoute = loc.pathname.startsWith("/app/controller");

  const ALL_TABS = [
    { key: "people", label: "People analytics", icon: BarChart3, requires: "admin" },
    { key: "assignments", label: "Assignments", icon: Send, requires: "admin" },
    { key: "meeting_templates", label: "Meeting templates", icon: MessageSquare, requires: "admin" },
    { key: "setup", label: "Setup", icon: SettingsIcon, requires: "admin" },
    { key: "members", label: "Members & seats", icon: Users, requires: "controller" },
    { key: "billing", label: "Billing & pricing", icon: DollarSign, requires: "controller" },
    { key: "analytics", label: "Usage analytics", icon: BarChart3, requires: "controller" },
    { key: "settings", label: "Organization", icon: SettingsIcon, requires: "controller" },
    { key: "vault", label: "Data Vault", icon: Archive, requires: "controller" },
  ];
  // Admin route: everyone with admin+ sees admin tabs. Controller route: only controller-tier tabs.
  const tabs = isControllerRoute
    ? ALL_TABS.filter((t) => t.requires === "controller")
    : ALL_TABS.filter((t) => t.requires === "admin");
  const [tab, setTab] = useState(isControllerRoute ? "members" : "people");

  if (!isAdmin) {
    return (
      <div className="p-10">
        <div className="bg-[#fff1ea] border border-[#ff4500]/30 rounded-2xl p-6 max-w-xl">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// 403 · Admin or Controller only</div>
          <h1 className="font-display font-black text-2xl mt-1">Access required</h1>
          <p className="text-sm text-[#1a1a1a]/75 mt-2">Ask your organization Controller to upgrade your role if you need access to this panel.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-5">
      <section className="hero-navy rounded-3xl p-6 lg:p-8 text-white overflow-hidden relative">
        <div className="absolute inset-0 dotted opacity-25" />
        <div className="absolute -top-20 -right-10 w-72 h-72 rounded-full bg-[#ff4500]/25 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1 backdrop-blur-sm">
            {isControllerRoute ? <Crown className="w-3 h-3 text-[#ff5f15]" /> : <ShieldCheck className="w-3 h-3 text-[#ff5f15]" />}
            <span className="font-mono text-[10px] uppercase tracking-[0.18em]">{isControllerRoute ? "CONTROLLER" : "ADMIN"} · {user?.org?.display_id}</span>
          </div>
          <h1 className="mt-3 font-display font-black text-3xl lg:text-4xl tracking-tight leading-tight">
            {isControllerRoute ? "Controller" : "Admin"} · {user?.org?.name}.
          </h1>
          <p className="mt-2 text-sm text-white/75 max-w-xl">
            {isController
              ? "Create paid seats, manage billing, monitor usage, configure form destinations. Seats are charged at creation — setup fee + first month's subscription bill immediately."
              : "Configure where completed forms are sent. To invite or remove members, ask a Controller."}
          </p>
        </div>
      </section>
      <div className="flex gap-1 flex-wrap border-b border-[#1a1a1a]/10">
        {tabs.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2 text-[12px] uppercase tracking-wider font-bold rounded-t-xl ${tab === t.key ? "bg-[#1a1a1a] text-white" : "text-[#1a1a1a]/65 hover:text-[#1a1a1a]"}`}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "members" && <MembersTab user={user} />}
      {tab === "people" && <PeopleTab user={user} />}
      {tab === "assignments" && <AssignmentsTab />}
      {tab === "meeting_templates" && <MeetingTemplatesTab />}
      {tab === "billing" && <BillingTab />}
      {tab === "analytics" && <AnalyticsTab />}
      {tab === "setup" && <SetupTab />}
      {tab === "settings" && <OrgSettingsTab user={user} />}
      {tab === "vault" && <DataVaultTab />}
    </div>
  );
}

function MembersTab({ user }) {
  const isController = user?.org_role === "controller";
  const [data, setData] = useState(null);
  const [bundleSeats, setBundleSeats] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const load = () => {
    api.get("/orgs/me").then((r) => setData(r.data)).catch(() => {});
    api.get("/orgs/me/bundle-seats").then((r) => setBundleSeats(r.data || [])).catch(() => setBundleSeats([]));
  };
  useEffect(() => { load(); }, []);
  if (!data) return <div className="font-mono text-[#1a1a1a]/55 text-[11px] uppercase tracking-wider">Loading…</div>;
  const { seats, members, invitations } = data;
  const activeMembers = members.filter((m) => !m.user?.deactivated_at);
  const deactivated = members.filter((m) => m.user?.deactivated_at);
  const controllerCount = activeMembers.filter((m) => m.membership.role === "controller").length;

  const inviteUrl = (t) => `${window.location.origin}/register?token=${t}`;
  const copyInvite = (t) => { navigator.clipboard.writeText(inviteUrl(t)); toast.success("Invitation link copied"); };
  const revoke = async (id) => {
    if (!window.confirm("Revoke this pending invitation?")) return;
    await api.delete(`/invitations/${id}`); toast.success("Revoked"); load();
  };
  const changeRole = async (uid, newRole) => {
    try { await api.patch(`/members/${uid}`, { role: newRole }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deactivate = async (uid, name) => {
    if (!window.confirm(`Deactivate ${name}? Their seat will free up and they can no longer log in. All their past signatures and audit records remain intact and immutable.`)) return;
    try { await api.post(`/members/${uid}/deactivate`); toast.success("Member deactivated · seat freed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const reactivate = async (uid) => {
    try { await api.post(`/members/${uid}/reactivate`); toast.success("Reactivated · seat charged"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const pct = Math.round((seats.used / Math.max(seats.limit, 1)) * 100);
  return (
    <div className="space-y-5">
      {controllerCount <= 1 && (
        <div className="bg-[#fff1ea] border border-[#ff4500]/30 rounded-xl p-3 flex items-center gap-2" data-testid="single-controller-warning">
          <AlertTriangle className="w-4 h-4 text-[#ff4500]" />
          <div className="text-[12px] text-[#1a1a1a]/85">
            <b>Only one Controller in this organization.</b> We strongly recommend at least two so billing &amp; user management isn&apos;t blocked if one Controller is unavailable.
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="surface-card rounded-2xl p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Seats</div>
          <div className="font-display font-bold text-base">Active seats</div>
          <div className="mt-3 flex items-end gap-2">
            <div className="font-display font-black text-5xl text-[#ff4500] leading-none">{seats.used}</div>
            <div className="text-sm text-[#1a1a1a]/65 mb-1">of {seats.limit} used</div>
          </div>
          <div className="mt-3 h-2 bg-[#f7f5f2] rounded-full overflow-hidden">
            <div className="h-full bg-[#ff4500]" style={{ width: `${Math.min(100, pct)}%` }} />
          </div>
          <div className="mt-2 text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">{seats.available} available · pending invites count toward the cap</div>
        </div>
        <div className="surface-card rounded-2xl p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Create paid seat</div>
              <div className="font-display font-bold text-base">Add a new member</div>
              <div className="text-[12px] text-[#1a1a1a]/65 mt-1">Setup fee + first month's subscription are <b>charged immediately</b> when you create a seat. You'll see the exact total before confirming.</div>
            </div>
            <button data-testid="admin-invite-open" onClick={() => setInviteOpen(true)}
              disabled={seats.available <= 0}
              className="btn-pill btn-pill-primary disabled:opacity-50">
              <Plus className="w-3.5 h-3.5" /> Create seat
            </button>
          </div>
          {invitations.filter((i) => !i.used_at).length === 0 ? (
            <div className="text-[12px] text-[#1a1a1a]/55 font-mono uppercase tracking-wider">No pending invitations.</div>
          ) : (
            <ul className="space-y-2">
              {invitations.filter((i) => !i.used_at).map((inv) => (
                <li key={inv.id} className="flex items-center justify-between bg-[#f7f5f2] rounded-xl px-3 py-2" data-testid={`invitation-${inv.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold truncate">{inv.email}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">
                      {inv.role} · setup ${inv.setup_fee_at_creation ?? 0} · ${inv.monthly_fee_at_creation ?? 0}/mo · expires {formatDate(inv.expires_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`invitation-copy-${inv.id}`} onClick={() => copyInvite(inv.token)} className="btn-pill btn-pill-ghost text-[10px]"><Copy className="w-3 h-3" /> Copy link</button>
                    <button data-testid={`invitation-revoke-${inv.id}`} onClick={() => revoke(inv.id)} className="btn-pill btn-pill-ghost text-[10px] text-[#ff4500]"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="surface-card rounded-2xl p-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-1">// Active members</div>
        <div className="font-display font-bold text-base mb-3">{activeMembers.length} active</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="members-table">
            <thead>
              <tr className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 text-left">
                <th className="py-1">Name</th><th>Email</th><th>Role</th><th>Joined</th><th></th>
              </tr>
            </thead>
            <tbody>
              {activeMembers.map((m) => (
                <tr key={m.membership.id} className="border-t border-[#1a1a1a]/05" data-testid={`member-row-${m.user.id}`}>
                  <td className="py-2 font-bold whitespace-nowrap">{m.user?.name}{m.user?.id === user?.id ? <span className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 ml-1">(you)</span> : null}</td>
                  <td className="text-[#1a1a1a]/75">{m.user?.email}</td>
                  <td>
                    {isController ? (
                      <select data-testid={`member-role-${m.user.id}`} value={m.membership.role} onChange={(e) => changeRole(m.user.id, e.target.value)}
                        disabled={m.user?.id === user?.id && m.membership.role === "controller" && controllerCount <= 1}
                        className="text-[11px] uppercase tracking-wider font-bold bg-white border border-[#1a1a1a]/15 rounded-full px-2 py-0.5">
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        <option value="controller">Controller</option>
                      </select>
                    ) : (
                      <span data-testid={`member-role-readonly-${m.user.id}`} className="text-[11px] uppercase tracking-wider font-bold text-[#1a1a1a]/70">{m.membership.role}</span>
                    )}
                  </td>
                  <td className="font-mono text-[10px] text-[#1a1a1a]/65 whitespace-nowrap">{formatDate(m.membership.created_at)}</td>
                  <td className="text-right">
                    <div className="inline-flex items-center gap-1">
                      <Link data-testid={`member-analytics-${m.user.id}`}
                        to={m.user?.id === user?.id ? "/app/me/analytics" : `/app/people/${m.user.id}/analytics`}
                        className="btn-pill btn-pill-ghost text-[10px]"><LineChart className="w-3 h-3" /> Analytics</Link>
                      {m.user?.id !== user?.id && (
                        <button data-testid={`member-deactivate-${m.user.id}`} onClick={() => deactivate(m.user.id, m.user.name)}
                          className="btn-pill btn-pill-ghost text-[10px] text-[#ff4500]"><UserMinus className="w-3 h-3" /> Deactivate</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {deactivated.length > 0 && (
        <div className="surface-card rounded-2xl p-5" data-testid="deactivated-members">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1a1a1a]/55 mb-1">// Deactivated · audit records preserved</div>
          <div className="font-display font-bold text-base mb-3">{deactivated.length} deactivated</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 text-left">
                  <th className="py-1">Name</th><th>Email</th><th>Was</th><th>Deactivated</th><th>By</th><th></th>
                </tr>
              </thead>
              <tbody>
                {deactivated.map((m) => (
                  <tr key={m.membership.id} className="border-t border-[#1a1a1a]/05 opacity-75">
                    <td className="py-2 font-bold">{m.user?.name}</td>
                    <td className="text-[#1a1a1a]/65">{m.user?.email}</td>
                    <td className="text-[10px] font-mono uppercase">{m.membership.role}</td>
                    <td className="font-mono text-[10px] text-[#1a1a1a]/55">{formatDate(m.user?.deactivated_at)}</td>
                    <td className="font-mono text-[10px] text-[#1a1a1a]/55">{m.user?.deactivated_by}</td>
                    <td className="text-right">
                      <button data-testid={`member-reactivate-${m.user.id}`} onClick={() => reactivate(m.user.id)}
                        className="btn-pill btn-pill-ghost text-[10px] text-[#1a8754]"><UserCheck className="w-3 h-3" /> Reactivate</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} onCreated={() => { setInviteOpen(false); load(); }} pricing={data.pricing} />

      {bundleSeats.length > 0 && (
        <div className="surface-card rounded-2xl p-5 mt-4" data-testid="bundle-seats-panel">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-[#a13ad6]" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#a13ad6]">// Pre-purchased seats</div>
              <div className="font-display font-bold text-base">Bundle — {bundleSeats.length} seat{bundleSeats.length === 1 ? "" : "s"}</div>
            </div>
            <div className="ml-auto text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">
              {bundleSeats.filter((s) => !s.filled_by_user_id).length} empty / {bundleSeats.filter((s) => s.filled_by_user_id).length} filled
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {bundleSeats.map((s, i) => {
              const filled = !!s.filled_by_user_id;
              return (
                <div key={s.id} data-testid={`bundle-seat-${i}`}
                  className={`p-3 rounded-lg border ${filled ? "bg-[#f7f5f2] border-[#1a1a1a]/10" : "border-dashed border-[#a13ad6]/40 bg-[#faf0ff]"}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#a13ad6]/15 text-[#a13ad6]">{s.seat_type}</span>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">
                      ${s.price_monthly}/mo
                    </span>
                    {filled ? (
                      <span className="text-[10px] uppercase tracking-wider font-bold text-[#1a8754] ml-auto">Filled</span>
                    ) : (
                      <button onClick={() => setInviteOpen(true)}
                        data-testid={`fill-bundle-seat-${i}`}
                        className="ml-auto text-[10px] uppercase tracking-wider text-[#a13ad6] font-bold hover:underline">
                        Fill seat
                      </button>
                    )}
                  </div>
                  {s.note && <div className="text-[10px] text-[#1a1a1a]/60 mt-1">{s.note}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function InviteDialog({ open, onClose, onCreated, pricing }) {
  const [form, setForm] = useState({ email: "", role: "user", position: "", confirm: false });
  const [busy, setBusy] = useState(false);
  const [createdLink, setCreatedLink] = useState("");
  const [charged, setCharged] = useState(null);
  useEffect(() => { if (open) { setForm({ email: "", role: "user", position: "", confirm: false }); setCreatedLink(""); setCharged(null); } }, [open]);
  const submit = async () => {
    if (!form.email.trim()) return toast.error("Email is required");
    if (!form.confirm) return toast.error("Please tick the confirmation checkbox below — this charge is billed immediately.");
    setBusy(true);
    try {
      const r = await api.post("/members/create", { email: form.email.trim(), role: form.role, position: form.position.trim() || null, confirm: true });
      setCharged(r.data.charged_today);
      const link = `${window.location.origin}/register?token=${r.data.invitation.token}`;
      setCreatedLink(link);
      navigator.clipboard.writeText(link).catch(() => {});
      toast.success(`Seat created · $${r.data.charged_today} charged today · invite link copied`);
      onCreated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const p = pricing?.[form.role] || {};
  const setup = Number(p.setup || 0);
  const monthly = Number(p.monthly || 0);
  const dueToday = setup + monthly;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl border-[#1a1a1a]/15 max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl flex items-center gap-2"><Mail className="w-5 h-5 text-[#ff4500]" /> Create paid seat</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-[12px] text-[#1a1a1a]/75">
            Seats are <b>billed immediately on creation</b> — your card on file is charged the setup fee + first month's subscription today.
            The new member will register at <b>/register?token=…</b> with their own email + password.
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">New member's email</label>
            <input data-testid="invite-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value, confirm: false })}
              placeholder="someone@company.com" type="email"
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#1a1a1a]" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Position / job title <span className="normal-case text-[10px] tracking-normal text-[#1a1a1a]/50">(optional — lands on the Org-Dev roster)</span></label>
            <input data-testid="invite-position" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })}
              placeholder="Line Operator · QA Inspector · Maintenance Tech …"
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#1a1a1a]" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Role to grant</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {[
                { k: "user", label: "User", hint: "Audits, actions, signing" },
                { k: "admin", label: "Admin", hint: "Forms · destinations · hubs" },
                { k: "controller", label: "Controller", hint: "Billing · user mgmt" },
              ].map(({ k, label, hint }) => {
                const on = form.role === k;
                const pp = pricing?.[k] || {};
                return (
                  <button key={k} data-testid={`invite-role-${k}`} type="button" onClick={() => setForm({ ...form, role: k, confirm: false })}
                    className={`text-left px-3 py-2 rounded-xl border ${on ? "bg-[#1a1a1a] text-white border-[#1a1a1a]" : "bg-white text-[#1a1a1a] border-[#1a1a1a]/15"}`}>
                    <div className="text-[11px] uppercase tracking-wider font-bold">{label}</div>
                    <div className={`text-[10px] mt-0.5 ${on ? "text-white/70" : "text-[#1a1a1a]/60"}`}>{hint}</div>
                    <div className={`text-[10px] font-mono mt-1 ${on ? "text-[#ff5f15]" : "text-[#ff4500]"}`}>${pp.setup ?? "—"} setup · ${pp.monthly ?? "—"}/mo</div>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Cost preview — only appears once email + role are picked */}
          {form.email.trim() && (
            <div className="bg-[#fff1ea] border-2 border-[#ff4500]/30 rounded-xl p-3 text-[12px]" data-testid="invite-cost-preview">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff4500] mb-2">// What you'll be charged TODAY</div>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <CostRow label="Setup fee (one-time)" value={`$${setup.toFixed(2)}`} />
                <CostRow label="First month subscription" value={`$${monthly.toFixed(2)}`} />
                <CostRow label="Due today" value={`$${dueToday.toFixed(2)}`} accent testId="invite-due-today" />
              </div>
              <div className="text-[11px] text-[#1a1a1a]/65">After today: $<b>{monthly.toFixed(2)}</b>/month until the seat is deactivated. Deactivating a member frees the seat and stops future charges.</div>
              <label className="mt-3 flex items-start gap-2 cursor-pointer">
                <input type="checkbox" data-testid="invite-confirm-checkbox"
                  checked={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.checked })}
                  className="mt-0.5 w-4 h-4 accent-[#ff4500]" />
                <span className="text-[12px] font-display font-bold">
                  I confirm <span className="text-[#ff4500]">${dueToday.toFixed(2)}</span> will be charged today to create a <span className="uppercase">{form.role}</span> seat for <span className="font-mono">{form.email.trim() || "(email)"}</span>.
                </span>
              </label>
            </div>
          )}
          {charged != null && (
            <div className="bg-[#e8f5ee] border border-[#1a8754]/30 rounded-xl p-3 text-[12px]" data-testid="invite-charged">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#1a8754] mb-1">// Charge complete</div>
              <div className="font-display font-bold">${Number(charged).toFixed(2)} charged. Seat reserved.</div>
            </div>
          )}
          {createdLink && (
            <div className="bg-[#f7f5f2] rounded-xl p-3" data-testid="invite-created">
              <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 mb-1">Registration link (auto-copied)</div>
              <div className="text-[11px] font-mono break-all">{createdLink}</div>
            </div>
          )}
        </div>
        <DialogFooter>
          <button data-testid="invite-submit" onClick={submit}
            disabled={busy || !form.email.trim() || !form.confirm}
            className="btn-pill btn-pill-primary disabled:opacity-50 disabled:cursor-not-allowed">
            <Send className="w-3.5 h-3.5" /> {busy ? "Charging…" : `Charge $${dueToday.toFixed(2)} & create`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CostRow({ label, value, accent, testId }) {
  return (
    <div data-testid={testId} className={`rounded-lg p-2 text-center ${accent ? "bg-[#ff4500] text-white" : "bg-white border border-[#1a1a1a]/10"}`}>
      <div className={`font-mono text-[9px] uppercase tracking-[0.18em] ${accent ? "text-white/80" : "text-[#1a1a1a]/55"}`}>{label}</div>
      <div className="font-display font-black text-lg mt-0.5">{value}</div>
    </div>
  );
}

function PeopleTab({ user }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "activity_score", dir: "desc" });
  useEffect(() => {
    api.get("/orgs/me/people-analytics").then((r) => setData(r.data)).catch(() => setData({ members: [] }));
  }, []);
  if (!data) return <div className="font-mono text-[#1a1a1a]/55 text-[11px] uppercase tracking-wider">Loading…</div>;
  const members = data.members || [];
  const filtered = !q.trim() ? members : members.filter((u) =>
    (u.name || "").toLowerCase().includes(q.toLowerCase())
    || (u.email || "").toLowerCase().includes(q.toLowerCase())
    || (u.employee_id || "").toLowerCase().includes(q.toLowerCase())
  );
  const sorted = [...filtered].sort((a, b) => {
    const av = a[sort.key], bv = b[sort.key];
    const dir = sort.dir === "asc" ? 1 : -1;
    if (av === bv) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return typeof av === "number" ? (av - bv) * dir : String(av).localeCompare(String(bv)) * dir;
  });
  const toggleSort = (key) => setSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" });

  const scoreBand = (n) => n >= 75 ? "#1a8754" : n >= 40 ? "#d6a900" : "#ff4500";

  const cols = [
    { key: "name", label: "Name" },
    { key: "role", label: "Role" },
    { key: "joined_at", label: "Joined" },
    { key: "activity_score", label: "Activity" },
    { key: "audits_completed", label: "Audits" },
    { key: "actions_created", label: "Actions" },
    { key: "actions_closed", label: "Closed" },
    { key: "open_items", label: "Open" },
    { key: "overdue_items", label: "Overdue" },
    { key: "last_login_at", label: "Last seen" },
  ];

  return (
    <div className="surface-card rounded-2xl p-5" data-testid="people-tab">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// People analytics · org roll-up</div>
          <div className="font-display font-bold text-base">Everyone in {user?.org?.name}</div>
          <div className="text-[11px] text-[#1a1a1a]/65 mt-0.5">
            Click any name to open their Command panel. Activity score is a weighted blend of logins, audits done, actions created &amp; closed, and hub comments.
          </div>
        </div>
        <input data-testid="people-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, employee id…"
          className="bg-white border border-[#1a1a1a]/15 rounded-full px-3 py-1.5 text-sm w-60" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="people-analytics-table">
          <thead>
            <tr className="text-[10px] font-mono uppercase tracking-wider text-white bg-[#1a1a1a] text-left">
              {cols.map((c) => (
                <th key={c.key} onClick={() => toggleSort(c.key)}
                    className="py-1.5 px-2 cursor-pointer hover:text-[#ff5f15] select-none whitespace-nowrap"
                    data-testid={`people-sort-${c.key}`}>
                  {c.label} {sort.key === c.key ? (sort.dir === "asc" ? "▲" : "▼") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((u) => {
              const link = u.user_id === user?.id ? "/app/me/analytics" : `/app/people/${u.user_id}/analytics`;
              return (
                <tr key={u.user_id} className="border-t border-[#1a1a1a]/06 hover:bg-[#faf8f4]"
                    data-testid={`people-row-${u.user_id}`}>
                  <td className="py-2 px-2">
                    <Link to={link} data-testid={`people-name-link-${u.user_id}`}
                          className="font-display font-bold text-[#1a1a1a] hover:text-[#ff4500]">
                      {u.name || u.email}
                    </Link>
                    <div className="text-[10px] font-mono text-[#1a1a1a]/50">{u.email}{u.employee_id ? ` · #${u.employee_id}` : ""}</div>
                  </td>
                  <td className="px-2 text-[10px] font-mono uppercase tracking-wider font-bold" style={{ color: u.role === "controller" ? "#ff4500" : u.role === "admin" ? "#1a1a1a" : "#666" }}>{u.role}</td>
                  <td className="px-2 text-[10px] font-mono text-[#1a1a1a]/55 whitespace-nowrap">{formatDate(u.joined_at)}</td>
                  <td className="px-2">
                    <div className="flex items-center gap-1.5">
                      <div className="flex-1 h-1.5 bg-[#f7f5f2] rounded-full overflow-hidden max-w-[60px]">
                        <div className="h-full" style={{ background: scoreBand(u.activity_score), width: `${u.activity_score}%` }} />
                      </div>
                      <span className="font-display font-bold text-[12px] tabular-nums" style={{ color: scoreBand(u.activity_score) }}>{u.activity_score}</span>
                    </div>
                  </td>
                  <td className="px-2 tabular-nums text-center">{u.audits_completed}</td>
                  <td className="px-2 tabular-nums text-center">{u.actions_created}</td>
                  <td className="px-2 tabular-nums text-center text-[#1a8754]">{u.actions_closed}</td>
                  <td className="px-2 tabular-nums text-center">{u.open_items}</td>
                  <td className="px-2 tabular-nums text-center font-bold" style={{ color: u.overdue_items > 0 ? "#ff4500" : "#1a1a1a" }}>{u.overdue_items}</td>
                  <td className="px-2 text-[10px] font-mono text-[#1a1a1a]/55 whitespace-nowrap">{u.last_login_at ? formatDate(u.last_login_at) : "—"}</td>
                </tr>
              );
            })}
            {sorted.length === 0 && <tr><td colSpan={cols.length} className="py-10 text-center text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">No people match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function BillingTab() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [prices, setPrices] = useState({});
  const load = () => api.get("/billing/summary").then((r) => { setData(r.data); setPrices(JSON.parse(JSON.stringify(r.data.pricing))); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return <div className="font-mono text-[#1a1a1a]/55 text-[11px] uppercase tracking-wider">Loading…</div>;
  const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const savePrices = async () => {
    try {
      await api.patch("/orgs/me/pricing", { pricing: prices });
      toast.success("Pricing updated");
      setEditing(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Kpi label="Monthly recurring" value={fmt(data.monthly_total)} accent />
        <Kpi label="Setup fees · charged" value={fmt(data.setup_charged_total)} />
        <Kpi label="Setup fees · pending tokens" value={fmt(data.setup_pending_total)} />
      </div>
      <div className="surface-card rounded-2xl p-5" data-testid="billing-breakdown">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Per-role pricing</div>
            <div className="font-display font-bold text-base">Seats × pricing</div>
          </div>
          {editing ? (
            <div className="flex gap-2">
              <button data-testid="pricing-cancel" onClick={() => { setEditing(false); setPrices(JSON.parse(JSON.stringify(data.pricing))); }} className="btn-pill btn-pill-ghost">Cancel</button>
              <button data-testid="pricing-save" onClick={savePrices} className="btn-pill btn-pill-primary">Save</button>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60 px-3 py-1.5 bg-[#f7f5f2] rounded-full border border-[#1a1a1a]/10">
              <Lock className="w-3 h-3" /> Rates set by platform · contact your account manager to change
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 text-left">
                <th className="py-1">Role</th><th>Setup fee</th><th>Monthly fee</th><th>Active</th><th>Pending</th><th className="text-right">Monthly subtotal</th>
              </tr>
            </thead>
            <tbody>
              {data.breakdown.map((row) => (
                <tr key={row.role} className="border-t border-[#1a1a1a]/05" data-testid={`billing-row-${row.role}`}>
                  <td className="py-2 font-display font-bold uppercase">{row.label}</td>
                  <td>{fmt(row.setup_fee)}</td>
                  <td>{fmt(row.monthly_fee)}</td>
                  <td className="font-display font-bold">{row.active_count}</td>
                  <td>{row.pending_invites}</td>
                  <td className="text-right font-display font-bold text-[#ff4500]">{fmt(row.monthly_subtotal)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-[#1a1a1a]/15">
                <td colSpan="5" className="py-2 text-right font-mono text-[10px] uppercase tracking-wider text-[#1a1a1a]/55">Total monthly recurring</td>
                <td className="text-right font-display font-black text-[#ff4500] text-lg">{fmt(data.monthly_total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">
          Stripe billing is not connected yet — figures are for transparency &amp; planning only.
        </div>
      </div>

      <div className="surface-card rounded-2xl p-5" data-testid="billing-history">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-1">// Activity</div>
        <div className="font-display font-bold text-base mb-3">Billing events</div>
        {data.history.length === 0 ? (
          <div className="text-[12px] text-[#1a1a1a]/55 font-mono uppercase tracking-wider">No activity yet.</div>
        ) : (
          <ul className="space-y-1.5">
            {data.history.slice(0, 20).map((h) => (
              <li key={h.id} className="flex items-center justify-between bg-[#f7f5f2] rounded-md px-3 py-1.5 text-[12px]">
                <span>
                  <span className="font-mono text-[10px] uppercase tracking-wider text-[#1a1a1a]/55 mr-2">{formatDate(h.created_at)}</span>
                  <b>{h.kind.replace("_", " ")}</b> · {h.role || ""} · by {h.actor_name || "—"}
                </span>
                <span className="font-display font-bold">
                  {h.kind === "seat_activated" && <span>+{fmt(h.setup_fee)} setup, +{fmt(h.monthly_fee)}/mo</span>}
                  {h.kind === "seat_deactivated" && <span className="text-[#1a8754]">−{fmt(h.monthly_fee_credit)}/mo</span>}
                  {h.kind === "seat_reactivated" && <span>+{fmt(h.monthly_fee)}/mo</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className={`surface-card rounded-2xl p-5 ${accent ? "ring-2 ring-[#ff4500]/30" : ""}`}>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// {label}</div>
      <div className={`font-display font-black text-3xl mt-1 ${accent ? "text-[#ff4500]" : "text-[#1a1a1a]"}`}>{value}</div>
    </div>
  );
}

function AnalyticsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/members/analytics").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="font-mono text-[#1a1a1a]/55 text-[11px] uppercase tracking-wider">Loading…</div>;
  const idle = data.members.filter((m) => m.idle && !m.deactivated_at).length;
  const neverLoggedIn = data.members.filter((m) => !m.last_login_at && !m.deactivated_at).length;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Kpi label="Idle ≥ 14 days" value={idle} accent />
        <Kpi label="Never logged in" value={neverLoggedIn} />
        <Kpi label="Total members" value={data.members.filter((m) => !m.deactivated_at).length} />
      </div>
      <div className="surface-card rounded-2xl p-5" data-testid="analytics-table">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-1">// Per-member usage</div>
        <div className="font-display font-bold text-base mb-3">Who is &amp; isn&apos;t using their seat</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 text-left">
                <th className="py-1">Name</th><th>Role</th><th>Last login</th><th>Logins</th><th>Actions</th><th>Audits</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.members.map((m) => (
                <tr key={m.user_id} className={`border-t border-[#1a1a1a]/05 ${m.deactivated_at ? "opacity-50" : ""}`} data-testid={`analytics-row-${m.user_id}`}>
                  <td className="py-2 font-bold whitespace-nowrap">{m.name}</td>
                  <td className="text-[10px] font-mono uppercase tracking-wider">{m.role}</td>
                  <td className="font-mono text-[10px] text-[#1a1a1a]/65">{m.last_login_at ? formatDateTime(m.last_login_at) : "—"}</td>
                  <td>{m.login_count}</td>
                  <td>{m.actions_created}</td>
                  <td>{m.runs_started}</td>
                  <td>
                    {m.deactivated_at ? (
                      <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1a1a1a]/10">Deactivated</span>
                    ) : m.idle ? (
                      <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#ff4500]/15 text-[#ff4500] font-bold">Idle {m.days_since_last_login != null ? `· ${m.days_since_last_login}d` : ""}</span>
                    ) : (
                      <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1a8754]/15 text-[#1a8754] font-bold">Active</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const DEST_TYPES = [
  { key: "email", label: "Email", icon: Mail, hint: "Comma-separated emails. Mocked SMTP — logs to delivery trail." },
  { key: "msteams", label: "MS Teams", icon: MessageSquare, hint: "Incoming webhook URL (mocked)." },
  { key: "sharepoint", label: "SharePoint", icon: FolderOpen, hint: "Folder URL or library path (mocked)." },
  { key: "other", label: "Other", icon: Webhook, hint: "Free-form notes. Mocked." },
];

function SetupTab() {
  return (
    <div className="space-y-4">
      <div className="mb-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Admin</div>
        <div className="font-display font-black text-3xl tracking-tight">Setup</div>
        <div className="text-sm text-[#1a1a1a]/65 mt-1">
          Standardise your audit vocabulary and configure where completed forms are sent.
        </div>
      </div>
      <Collapsible id="tags" title="Tags · controlled vocabulary" subtitle="Prevent typos across audits — one canonical list per category." defaultOpen>
        <TagsManager />
      </Collapsible>
      <Collapsible id="destinations" title="Destinations" subtitle="Where signed audit + meeting PDFs are sent on close-out.">
        <DestinationsTab />
      </Collapsible>
    </div>
  );
}

function Collapsible({ id, title, subtitle, defaultOpen, children }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <section className="surface-card rounded-2xl overflow-hidden" data-testid={`collapsible-${id}`}>
      <button onClick={() => setOpen(!open)} data-testid={`collapsible-toggle-${id}`}
        className="w-full flex items-start gap-3 p-5 text-left hover:bg-[#f7f5f2] transition-colors">
        <div className={`mt-1 w-3 h-3 border-r-2 border-b-2 border-[#ff4500] transition-transform ${open ? "rotate-45" : "-rotate-45"}`} />
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-xl tracking-tight" data-testid={`collapsible-title-${id}`}>{title}</div>
          {subtitle && <div className="text-[13px] text-[#1a1a1a]/65 mt-0.5">{subtitle}</div>}
        </div>
      </button>
      {open && <div className="border-t border-[#1a1a1a]/10 p-5">{children}</div>}
    </section>
  );
}

function TagsManager() {
  const [byCat, setByCat] = useState({});
  const [customCats, setCustomCats] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [newCat, setNewCat] = useState("");
  const load = () => api.get("/tags").then((r) => setByCat(r.data || {})).catch(() => {});
  useEffect(() => {
    load();
    try { setCustomCats(JSON.parse(localStorage.getItem("hubcmd_custom_tag_cats") || "[]")); } catch (e) { /* ignore */ }
  }, []);
  const addTag = async (cat) => {
    const label = (drafts[cat] || "").trim();
    if (!label) return;
    try {
      await api.post("/tags", { category: cat, label });
      setDrafts({ ...drafts, [cat]: "" });
      toast.success(`${cat} tag added`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeTag = async (id, label) => {
    if (!window.confirm(`Delete tag "${label}"? Existing records that use it will keep their reference.`)) return;
    try { await api.delete(`/tags/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const addCustomCategory = () => {
    const k = newCat.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
    if (!k) return toast.error("Category name required");
    if (["equipment","location","department","type"].includes(k) || customCats.some(c => c.k === k)) {
      return toast.error("Category already exists");
    }
    const next = [...customCats, { k, label: newCat.trim() }];
    setCustomCats(next);
    localStorage.setItem("hubcmd_custom_tag_cats", JSON.stringify(next));
    setNewCat("");
    toast.success("Category added");
  };
  const CATS = [
    { k: "equipment", label: "Equipment", hint: "Press 1, Press 2, Extruder 12, CNC-A5, Conveyor West…" },
    { k: "location", label: "Location", hint: "Breakroom, Building Exterior, Warehouse B, Loading Dock…" },
    { k: "department", label: "Department", hint: "Quality, HR, Finance, Shipping, Maintenance…" },
    { k: "type", label: "Type", hint: "Documentation, Contaminant, Safety, Onboarding, SOP, Training…" },
    ...customCats.map(c => ({ k: c.k, label: c.label, hint: "Your custom category" })),
  ];
  return (
    <div data-testid="tags-manager">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {CATS.map(({ k, label, hint }) => (
          <div key={k} className="bg-[#f7f5f2] rounded-xl p-3" data-testid={`tags-${k}-card`}>
            <div className="flex items-center justify-between mb-1">
              <div className="font-display font-black text-sm">{label}</div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">{(byCat[k] || []).length} tag(s)</div>
            </div>
            <div className="text-[11px] text-[#1a1a1a]/60 mb-2">{hint}</div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {(byCat[k] || []).length === 0 && <span className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/45">None yet</span>}
              {(byCat[k] || []).map((t) => (
                <span key={t.id} className="inline-flex items-center gap-1 bg-white border border-[#1a1a1a]/15 text-[11px] font-bold px-2 py-0.5 rounded-full"
                  data-testid={`tag-${k}-${t.id}`}>
                  {t.label}
                  <button onClick={() => removeTag(t.id, t.label)} className="text-[#ff4500] hover:opacity-70 -mr-0.5"
                    data-testid={`tag-remove-${k}-${t.id}`}>×</button>
                </span>
              ))}
            </div>
            <div className="flex gap-1.5">
              <input data-testid={`tag-input-${k}`} value={drafts[k] || ""}
                onChange={(e) => setDrafts({ ...drafts, [k]: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(k); } }}
                placeholder={`Add ${label.toLowerCase()}…`}
                className="flex-1 bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1 text-sm" />
              <button onClick={() => addTag(k)} data-testid={`tag-add-${k}`}
                className="btn-pill btn-pill-primary text-[11px]">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
          </div>
        ))}
      </div>
      {/* Add custom category */}
      <div className="mt-4 bg-[#fff1ea] border-2 border-[#ff4500]/25 rounded-xl p-3" data-testid="add-tag-category">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-1">// Need a new category?</div>
        <div className="flex gap-1.5 items-center">
          <input data-testid="new-tag-cat-input" value={newCat}
            onChange={(e) => setNewCat(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomCategory(); } }}
            placeholder="e.g. Product line, Shift, Vendor"
            className="flex-1 bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1 text-sm" />
          <button onClick={addCustomCategory} data-testid="new-tag-cat-add"
            className="btn-pill btn-pill-primary text-[11px]">
            <Plus className="w-3 h-3" /> Add category
          </button>
        </div>
      </div>
    </div>
  );
}

function DestinationsTab() {
  const [list, setList] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/destinations").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!window.confirm("Delete this destination?")) return;
    await api.delete(`/destinations/${id}`); toast.success("Deleted"); load();
  };
  return (
    <div className="space-y-5">
      <div className="surface-card rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Form distribution — routed by report type</div>
            <div className="font-display font-bold text-base">Where do closed reports go?</div>
            <p className="text-[12px] text-[#1a1a1a]/65 mt-1">Pick which report types trigger each destination. A single report can fan out to multiple destinations, and any user who&apos;s already an attendee won&apos;t be double-emailed.</p>
          </div>
          <button data-testid="destination-add" onClick={() => { setEditing(null); setOpenDialog(true); }} className="btn-pill btn-pill-primary">
            <Plus className="w-3.5 h-3.5" /> Add destination
          </button>
        </div>
        {list.length === 0 ? (
          <div className="bg-[#f7f5f2] rounded-xl p-6 text-center">
            <div className="font-display font-bold">No destinations yet</div>
            <div className="text-[12px] text-[#1a1a1a]/65 mt-1">Add an email list to auto-send closed meeting reports and audit PDFs to the right stakeholders.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {list.map((d) => {
              const meta = DEST_TYPES.find((t) => t.key === d.type);
              const Icon = meta?.icon || Send;
              const rtypes = d.report_types || [];
              return (
                <div key={d.id} className="bg-[#f7f5f2] rounded-xl p-4 border border-[#1a1a1a]/05" data-testid={`destination-${d.id}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center"><Icon className="w-4 h-4 text-[#ff4500]" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-bold text-sm truncate">{d.name}</div>
                      <div className="font-mono text-[10px] uppercase tracking-wider text-[#1a1a1a]/55">{d.display_id} · {meta?.label}</div>
                    </div>
                  </div>
                  <div className="text-[11px] text-[#1a1a1a]/65 mt-2 break-all">
                    {d.type === "email" && (Array.isArray(d.config?.to) ? d.config.to.join(", ") : d.config?.to)}
                    {d.type === "msteams" && (d.config?.webhook_url ? d.config.webhook_url.slice(0, 50) + "…" : "—")}
                    {d.type === "sharepoint" && (d.config?.sharepoint_url || d.config?.folder || "—")}
                    {d.type === "other" && (d.config?.notes || "—")}
                  </div>
                  {rtypes.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1" data-testid={`destination-${d.id}-report-types`}>
                      {rtypes.slice(0, 4).map((rt) => (
                        <span key={rt} className="text-[9px] font-mono uppercase tracking-wider font-bold bg-[#ff4500]/15 text-[#ff4500] px-1.5 py-0.5 rounded-full">
                          {REPORT_TYPE_LABELS[rt] || rt}
                        </span>
                      ))}
                      {rtypes.length > 4 && <span className="text-[9px] font-mono text-[#1a1a1a]/55">+{rtypes.length - 4} more</span>}
                    </div>
                  )}
                  <div className="mt-2 flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider">
                    <span className="px-2 py-0.5 rounded-full bg-white">{d.active ? "ACTIVE" : "PAUSED"}</span>
                    {d.type !== "email" && <span className="px-2 py-0.5 rounded-full bg-white text-[#ff4500]">MOCKED</span>}
                    {rtypes.length === 0 && <span className="px-2 py-0.5 rounded-full bg-white text-[#d6a900]">NO TYPES</span>}
                  </div>
                  <div className="mt-3 flex gap-1">
                    <button data-testid={`destination-edit-${d.id}`} onClick={() => { setEditing(d); setOpenDialog(true); }} className="btn-pill btn-pill-ghost text-[10px]">Edit</button>
                    <button data-testid={`destination-delete-${d.id}`} onClick={() => del(d.id)} className="btn-pill btn-pill-ghost text-[10px] text-[#ff4500]"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <DestinationDialog open={openDialog} onClose={() => setOpenDialog(false)} editing={editing} onSaved={() => { setOpenDialog(false); load(); }} />
    </div>
  );
}

// Iter29 — report type keys used to route destinations. Kept in sync with backend logic
// in routers/formal_meetings.py close_formal_meeting (`meeting_<style>` + `meeting_any`).
const REPORT_TYPE_LABELS = {
  // Meetings
  meeting_any: "All meetings (any style)",
  meeting_management_review: "Meeting · Monthly Management Review",
  meeting_problem_solving: "Meeting · Problem Solving",
  meeting_effectiveness_review: "Meeting · Effectiveness Review",
  meeting_safety: "Meeting · Safety Stand-Down",
  meeting_custom: "Meeting · Custom",
  // Audits / checklists
  audit_any: "All audits (any template)",
  checklist_any: "All checklist runs",
  // V&V
  vv_any: "All V&V reports",
  vv_verification_completed: "V&V · Verification completed",
  vv_validation_completed: "V&V · Validation completed",
  // Coverage
  coverage_any: "Compliance Coverage reports",
  // Work requests
  work_request: "Work request (open)",
  work_request_completed: "Work request · closed (completed)",
  work_request_denied: "Work request · closed (denied)",
};

function DestinationDialog({ open, onClose, editing, onSaved }) {
  const [form, setForm] = useState({ name: "", type: "email", to: "", webhook_url: "", sharepoint_url: "", notes: "", active: true, report_types: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    if (editing) {
      const cfg = editing.config || {};
      setForm({
        name: editing.name, type: editing.type, active: editing.active !== false,
        to: Array.isArray(cfg.to) ? cfg.to.join(", ") : (cfg.to || ""),
        webhook_url: cfg.webhook_url || "", sharepoint_url: cfg.sharepoint_url || cfg.folder || "",
        notes: cfg.notes || "",
        report_types: editing.report_types || [],
      });
    } else {
      setForm({ name: "", type: "email", to: "", webhook_url: "", sharepoint_url: "", notes: "", active: true, report_types: [] });
    }
  }, [open, editing]);
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (form.report_types.length === 0) return toast.error("Pick at least one report type this destination should receive.");
    const cfg = {};
    if (form.type === "email") cfg.to = form.to.split(",").map((s) => s.trim()).filter(Boolean);
    if (form.type === "msteams") cfg.webhook_url = form.webhook_url;
    if (form.type === "sharepoint") cfg.sharepoint_url = form.sharepoint_url;
    if (form.type === "other") cfg.notes = form.notes;
    const payload = { name: form.name, type: form.type, config: cfg, active: form.active, report_types: form.report_types };
    setBusy(true);
    try {
      if (editing) await api.patch(`/destinations/${editing.id}`, payload);
      else await api.post("/destinations", payload);
      toast.success("Destination saved"); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const toggleReportType = (key) => {
    const on = form.report_types.includes(key);
    setForm({ ...form, report_types: on ? form.report_types.filter((k) => k !== key) : [...form.report_types, key] });
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl rounded-2xl border-[#1a1a1a]/15">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl flex items-center gap-2"><Send className="w-5 h-5 text-[#ff4500]" /> {editing ? "Edit destination" : "Add destination"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Destination label</label>
            <input data-testid="dest-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. QA Inbox · Plant Leadership Teams" className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 mt-1">Internal name your team will recognise when picking destinations on close-out.</div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Type</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {DEST_TYPES.map((t) => {
                const Icon = t.icon; const on = form.type === t.key;
                return (
                  <button key={t.key} data-testid={`dest-type-${t.key}`} type="button" onClick={() => setForm({ ...form, type: t.key })}
                    className={`px-2 py-2 rounded-xl text-[11px] uppercase tracking-wider font-bold flex items-center justify-center gap-1.5 border ${on ? "bg-[#1a1a1a] text-white border-[#1a1a1a]" : "bg-white text-[#1a1a1a] border-[#1a1a1a]/15"}`}>
                    <Icon className="w-3.5 h-3.5" /> {t.label}
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 mt-1">{DEST_TYPES.find((t) => t.key === form.type)?.hint}</div>
          </div>
          {form.type === "email" && (
            <div><label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Email list (comma-separated)</label>
              <textarea data-testid="dest-to" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })}
                placeholder="qa@acme.com, plant-supervisor@acme.com" className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm h-20" /></div>
          )}
          {form.type === "msteams" && (
            <div><label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Incoming webhook URL <span className="text-[#ff4500]">· MOCKED</span></label>
              <input data-testid="dest-webhook" value={form.webhook_url} onChange={(e) => setForm({ ...form, webhook_url: e.target.value })}
                placeholder="https://outlook.office.com/webhook/…" className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" /></div>
          )}
          {form.type === "sharepoint" && (
            <div><label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">SharePoint folder URL <span className="text-[#ff4500]">· MOCKED</span></label>
              <input data-testid="dest-sharepoint" value={form.sharepoint_url} onChange={(e) => setForm({ ...form, sharepoint_url: e.target.value })}
                placeholder="https://acme.sharepoint.com/sites/audits/…" className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" /></div>
          )}
          {form.type === "other" && (
            <div><label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Routing notes <span className="text-[#ff4500]">· MOCKED</span></label>
              <textarea data-testid="dest-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Describe what should happen with the PDF…" className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm h-20" /></div>
          )}
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Which report types should this destination receive? *</label>
            <div className="text-[10px] font-mono text-[#1a1a1a]/50 mb-2">Pick any that apply. When one of these reports closes, its PDF/link fans out to this destination. Users already on the attendee list won&apos;t be double-emailed.</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="dest-report-types">
              {Object.entries(REPORT_TYPE_LABELS).map(([key, label]) => {
                const on = form.report_types.includes(key);
                return (
                  <label key={key} className={`inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md border cursor-pointer text-[12px] ${on ? "bg-[#ff4500]/10 border-[#ff4500] text-[#1a1a1a] font-semibold" : "bg-white border-[#1a1a1a]/15 text-[#1a1a1a]/70"}`}>
                    <input type="checkbox" checked={on} onChange={() => toggleReportType(key)}
                      data-testid={`dest-report-type-${key}`}
                      className="accent-[#ff4500]" />
                    {label}
                  </label>
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="dest-active" id="dest-active" type="checkbox" checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            <label htmlFor="dest-active" className="text-[11px] uppercase tracking-wider font-mono text-[#1a1a1a]/65">Active</label>
          </div>
        </div>
        <DialogFooter>
          <button data-testid="dest-submit" onClick={submit} disabled={busy} className="btn-pill btn-pill-primary">{busy ? "Saving…" : "Save destination"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OrgSettingsTab({ user }) {
  const [org, setOrg] = useState(user?.org || null);
  const [form, setForm] = useState({
    name: "", seat_limit: 50, active_standards: [],
    pillars: [], code_prefixes: [],  // iter51 — Doc Library customization
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/orgs/me").then((r) => {
      setOrg(r.data.org);
      setForm({
        name: r.data.org.name,
        seat_limit: r.data.org.seat_limit,
        active_standards: r.data.org.active_standards || [],
        pillars: r.data.org.pillars && r.data.org.pillars.length ? r.data.org.pillars : DEFAULT_PILLARS,
        code_prefixes: r.data.org.code_prefixes && r.data.org.code_prefixes.length ? r.data.org.code_prefixes : DEFAULT_CODE_PREFIXES,
      });
    }).catch(() => {});
  }, []);
  if (!org) return null;
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch("/orgs/me", {
        name: form.name,
        seat_limit: Number(form.seat_limit),
        active_standards: form.active_standards,
        pillars: form.pillars,
        code_prefixes: form.code_prefixes,
      });
      setOrg(r.data);
      toast.success("Organization updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const toggleStd = (k) => setForm((s) => ({
    ...s,
    active_standards: s.active_standards.includes(k)
      ? s.active_standards.filter((x) => x !== k)
      : [...s.active_standards, k],
  }));
  const STANDARDS = [
    ["sqf", "SQF"],
    ["iso22000", "ISO 22000"],
    ["iso9001", "ISO 9001"],
    ["iso14001", "ISO 14001"],
    ["iso45001", "ISO 45001"],
    ["fsma", "FSMA"],
    ["brc", "BRCGS"],
    ["haccp", "HACCP"],
    ["gfsi", "GFSI"],
  ];
  return (
    <div className="surface-card rounded-2xl p-5 max-w-2xl">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-1">// Organization</div>
      <div className="font-display font-bold text-base mb-3 flex items-center gap-2"><Building2 className="w-4 h-4" /> {org.display_id}</div>
      <div className="space-y-4">
        <div>
          <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Organization name</label>
          <input data-testid="org-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Seat cap (hard limit)</label>
          <input data-testid="org-seat-limit" type="number" min="1" value={form.seat_limit} onChange={(e) => setForm({ ...form, seat_limit: e.target.value })}
            className="w-32 bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
          <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 mt-1">Stripe billing not connected — Controllers can adjust freely.</div>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Applicable standards</label>
          <div className="text-[11px] text-[#1a1a1a]/65 mb-2">
            Selecting standards filters the Compliance Catalog, V&amp;V templates and Checklist templates to only what applies to your org. Untagged items always remain visible under the "Untagged" chip.
          </div>
          <div className="flex flex-wrap gap-2" data-testid="org-standards">
            {STANDARDS.map(([k, l]) => {
              const on = form.active_standards.includes(k);
              return (
                <button key={k} type="button" data-testid={`org-std-${k}`}
                  onClick={() => toggleStd(k)}
                  className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider font-bold transition-colors ${
                    on ? "bg-[#ff4500] text-white" : "bg-[#f7f5f2] text-[#1a1a1a]/70 hover:bg-[#1a1a1a]/8"
                  }`}>
                  {l}
                </button>
              );
            })}
          </div>
          {form.active_standards.length === 0 && (
            <div className="mt-2 text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/45">
              None selected — all standards shown (no filter applied)
            </div>
          )}
        </div>

        {/* iter51 — Doc Library Pillar/Dept & Code Prefix customization */}
        <div className="border-t border-[#1a1a1a]/08 pt-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-1">// Doc Library vocabulary</div>
          <div className="text-[11px] text-[#1a1a1a]/65 mb-3">
            Customize the Pillar/Dept list and Code Prefix list your team sees when creating documents.
            These lists appear in the &quot;New document&quot; dialog and Doc Library filters.
          </div>
          <ListEditor
            label="Pillars / Departments"
            testId="pillars-editor"
            items={form.pillars}
            placeholder="e.g. Maintenance"
            onChange={(next) => setForm({ ...form, pillars: next })}
          />
          <PrefixEditor
            items={form.code_prefixes}
            onChange={(next) => setForm({ ...form, code_prefixes: next })}
          />
        </div>

        <button data-testid="org-save" onClick={save} disabled={busy} className="btn-pill btn-pill-primary">{busy ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
}

/** AssignmentsTab — multi-select destinations (Hubs first A-Z, then People A-Z),
 *  multi-select audit templates, due date, recurrence.
 */
function AssignmentsTab() {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [existing, setExisting] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [selectedTeamIds, setSelectedTeamIds] = useState(new Set());
  const [selectedTemplateIds, setSelectedTemplateIds] = useState(new Set());
  const [dueDate, setDueDate] = useState("");
  const [recurrence, setRecurrence] = useState("none");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/users/visible-to-me").then((r) => setUsers(r.data || [])).catch(() => {});
    api.get("/teams").then((r) => setTeams(r.data || [])).catch(() => {});
    api.get("/checklist-templates").then((r) => setTemplates(r.data || [])).catch(() => {});
    // Pull recent assignments per hub for context
    api.get("/me/assignments").then((r) => setExisting(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggle = (set, setSet, id) => {
    const n = new Set(set);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSet(n);
  };
  const sortedTeams = [...teams].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const sortedUsers = [...users].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const sortedTemplates = [...templates].sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const canSubmit = selectedTemplateIds.size > 0 && (selectedTeamIds.size > 0 || selectedUserIds.size > 0);

  const submit = async () => {
    if (!canSubmit) return toast.error("Pick at least one destination and one audit.");
    setBusy(true);
    try {
      // Create one assignment per audit template — the backend accepts a single template_id per doc.
      const tplIds = Array.from(selectedTemplateIds);
      for (const tid of tplIds) {
        await api.post("/assignments", {
          template_id: tid,
          assignee_user_ids: Array.from(selectedUserIds),
          assignee_team_ids: Array.from(selectedTeamIds),
          due_date: dueDate || null,
          recurrence,
          note,
        });
      }
      toast.success(`Assigned ${tplIds.length} audit${tplIds.length === 1 ? "" : "s"} to ${selectedTeamIds.size + selectedUserIds.size} destination${(selectedTeamIds.size + selectedUserIds.size) === 1 ? "" : "s"}.`);
      setSelectedTemplateIds(new Set());
      setSelectedTeamIds(new Set());
      setSelectedUserIds(new Set());
      setDueDate(""); setRecurrence("none"); setNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create assignments");
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="assignments-tab">
      <div className="surface-card rounded-2xl p-5 lg:col-span-2 space-y-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// New assignment</div>
          <div className="font-display font-bold text-base">Assign audits to hubs &amp; people</div>
          <div className="text-[11px] text-[#1a1a1a]/65 mt-0.5">Pick one or more destinations (hubs first, then individuals) and one or more audit templates. Every selected audit is delivered to every selected destination.</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Destinations · Hubs first, then people ({selectedTeamIds.size + selectedUserIds.size})</label>
            <div className="border border-[#1a1a1a]/15 rounded-xl bg-white max-h-80 overflow-auto scrollbar-thin" data-testid="assignment-destinations">
              {sortedTeams.length > 0 && (
                <>
                  <div className="px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#ff4500] bg-[#fff1ea]">// Hubs</div>
                  {sortedTeams.map((t) => {
                    const on = selectedTeamIds.has(t.id);
                    return (
                      <label key={t.id} data-testid={`assign-team-${t.id}`} className={`flex items-center gap-2 px-3 py-1.5 hover:bg-[#faf8f4] cursor-pointer ${on ? "bg-[#fff1ea]" : ""}`}>
                        <input type="checkbox" checked={on} onChange={() => toggle(selectedTeamIds, setSelectedTeamIds, t.id)} className="accent-[#ff4500]" />
                        <Users className="w-3.5 h-3.5 text-[#ff4500]" />
                        <span className="text-[12px] font-medium flex-1 truncate">{t.name}</span>
                        <span className="text-[10px] font-mono text-[#1a1a1a]/45">{t.members_count ?? "—"} mbr</span>
                      </label>
                    );
                  })}
                </>
              )}
              {sortedUsers.length > 0 && (
                <>
                  <div className="px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#1a1a1a]/55 bg-[#f7f5f2]">// Individuals</div>
                  {sortedUsers.map((u) => {
                    const on = selectedUserIds.has(u.id);
                    return (
                      <label key={u.id} data-testid={`assign-user-${u.id}`} className={`flex items-center gap-2 px-3 py-1.5 hover:bg-[#faf8f4] cursor-pointer ${on ? "bg-[#fff1ea]" : ""}`}>
                        <input type="checkbox" checked={on} onChange={() => toggle(selectedUserIds, setSelectedUserIds, u.id)} className="accent-[#ff4500]" />
                        <div className="w-4 h-4 rounded-full bg-[#f0ede6] text-[#1a1a1a] text-[8px] font-display font-black flex items-center justify-center">{(u.name || "?").slice(0,1)}</div>
                        <span className="text-[12px] font-medium flex-1 truncate">{u.name}</span>
                        <span className="text-[10px] font-mono text-[#1a1a1a]/45 truncate max-w-[100px]">{u.email}</span>
                      </label>
                    );
                  })}
                </>
              )}
            </div>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Audit templates ({selectedTemplateIds.size})</label>
            <div className="border border-[#1a1a1a]/15 rounded-xl bg-white max-h-80 overflow-auto scrollbar-thin" data-testid="assignment-templates">
              {sortedTemplates.length === 0 && <div className="text-[11px] text-[#1a1a1a]/55 p-3">No checklist templates yet.</div>}
              {sortedTemplates.map((t) => {
                const on = selectedTemplateIds.has(t.id);
                return (
                  <label key={t.id} data-testid={`assign-tpl-${t.id}`} className={`flex items-center gap-2 px-3 py-1.5 hover:bg-[#faf8f4] cursor-pointer ${on ? "bg-[#fff1ea]" : ""}`}>
                    <input type="checkbox" checked={on} onChange={() => toggle(selectedTemplateIds, setSelectedTemplateIds, t.id)} className="accent-[#ff4500]" />
                    <span className="text-[12px] font-medium flex-1 truncate">{t.name}</span>
                    <span className="text-[10px] font-mono text-[#1a1a1a]/45">{t.items?.length || 0} items</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Due date</label>
            <input data-testid="assign-due-date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Recurrence</label>
            <select data-testid="assign-recurrence" value={recurrence} onChange={(e) => setRecurrence(e.target.value)}
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm">
              <option value="none">One-time</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Note (optional)</label>
            <input data-testid="assign-note" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Shown on assignee's calendar…" className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
          </div>
        </div>

        <button data-testid="assign-submit" onClick={submit} disabled={!canSubmit || busy}
          className="btn-pill btn-pill-primary disabled:opacity-50">
          <Send className="w-3.5 h-3.5" /> {busy ? "Assigning…" : `Assign ${selectedTemplateIds.size} audit${selectedTemplateIds.size === 1 ? "" : "s"} → ${selectedTeamIds.size + selectedUserIds.size} destination${(selectedTeamIds.size + selectedUserIds.size) === 1 ? "" : "s"}`}
        </button>
      </div>

      <div className="surface-card rounded-2xl p-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-1">// Recent assignments</div>
        <div className="font-display font-bold text-base mb-3">Last {existing.length} on your hubs</div>
        <ul className="space-y-2 max-h-[420px] overflow-auto scrollbar-thin" data-testid="assignments-recent">
          {existing.length === 0 && <li className="text-[11px] text-[#1a1a1a]/55">No assignments visible to you yet.</li>}
          {existing.map((a) => (
            <li key={a.id} className="bg-[#f7f5f2] rounded-xl px-3 py-2 border border-[#1a1a1a]/05">
              <div className="text-[11px] font-mono text-[#ff4500] font-bold">{a.display_id || a.id.slice(0,8)}</div>
              <div className="text-[13px] font-display font-bold">{a.template_name}</div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">
                {a.recurrence !== "none" ? `${a.recurrence} · ` : ""}{a.due_date ? `due ${formatDate(a.due_date)}` : "no due"} · by {a.created_by_name}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Iter30 — Data Vault: 5-step archive + permanent delete (Controller only)
// ---------------------------------------------------------------------------
function DataVaultTab() {
  const [preview, setPreview] = useState(null);
  const [step, setStep] = useState(1); // 1..5
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [phrase1, setPhrase1] = useState("");
  const [phrase2, setPhrase2] = useState("");
  const [bundle, setBundle] = useState(null);
  const [downloaded, setDownloaded] = useState(false);
  const [downloadConfirmed, setDownloadConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const [bundles, setBundles] = useState([]);

  const load = async () => {
    try {
      const [p, l, b] = await Promise.all([
        api.get("/data-vault/preview"),
        api.get("/data-vault/audit-log"),
        api.get("/data-vault/bundles"),
      ]);
      setPreview(p.data); setLog(l.data || []); setBundles(b.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load Data Vault");
    }
  };
  useEffect(() => { load(); }, []);

  const totalSelected = preview
    ? preview.collections.filter((c) => selectedKeys.includes(c.key)).reduce((s, c) => s + c.count, 0)
    : 0;

  const prepareBundle = async () => {
    if (phrase1 !== "ARCHIVE AND REMOVE") {
      toast.error("Type the phrase exactly: ARCHIVE AND REMOVE");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/data-vault/prepare-bundle", {
        collection_keys: selectedKeys,
        older_than_days: 90,
        confirm_phrase: phrase1,
      });
      setBundle(r.data);
      toast.success(`Bundle prepared · ${r.data.item_count} items · ${(r.data.byte_size / 1024).toFixed(1)} KB`);
      setStep(3);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const doDownload = () => {
    if (!bundle) return;
    const token = localStorage.getItem("opscmd_token") || "";
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/data-vault/bundle/${bundle.bundle_id}/download`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error("dl fail"); return r.blob(); })
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `hubcmd-archive-${bundle.bundle_id.slice(0,8)}.zip`;
        link.click();
        setDownloaded(true);
        toast.success("Downloaded — please save it somewhere safe before continuing.");
      }).catch(() => toast.error("Download failed"));
  };

  const confirmDownload = async () => {
    if (!downloaded) { toast.error("Click Download first"); return; }
    try {
      await api.post("/data-vault/bundle/confirm-download", { bundle_id: bundle.bundle_id });
      setDownloadConfirmed(true);
      toast.success("Receipt confirmed · you can now proceed to Step 5");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const executeDelete = async () => {
    const expected = `PERMANENTLY DELETE ${bundle.item_count} ITEMS`;
    if (phrase2 !== expected) { toast.error(`Type exactly: ${expected}`); return; }
    if (!window.confirm(`This will PERMANENTLY delete ${bundle.item_count} records.\n\nYou've confirmed you downloaded the archive on ${new Date().toLocaleString()}.\n\nProceed?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/data-vault/bundle/execute-delete", { bundle_id: bundle.bundle_id, confirm_phrase: phrase2 });
      toast.success(`Deleted · sha256 receipt ${r.data.sha256.slice(0,12)}…`);
      setStep(1); setSelectedKeys([]); setPhrase1(""); setPhrase2("");
      setBundle(null); setDownloaded(false); setDownloadConfirmed(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!preview) return <div className="p-4 text-sm text-[#1a1a1a]/60">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="data-vault-tab">
      <div className="surface-card rounded-2xl p-5">
        <div className="flex items-center gap-2 text-[#ff4500]">
          <Archive className="w-4 h-4" />
          <div className="font-mono text-[10px] uppercase tracking-[0.2em]">// Controller-only · immutable audit log</div>
        </div>
        <div className="font-display font-black text-xl mt-1">Data Vault — 90-day archive & permanent delete</div>
        <p className="text-[13px] text-[#1a1a1a]/75 mt-1 max-w-3xl">
          Free up storage by downloading closed records older than 90 days and permanently removing them.
          Every step is documented in an immutable audit log so nobody can later claim documents were lost without acknowledgement.
        </p>
        <div className="mt-3 flex gap-1 flex-wrap">
          {[1,2,3,4,5].map((n) => (
            <div key={n} data-testid={`vault-step-indicator-${n}`}
              className={`text-[10px] font-mono uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border ${step === n ? "bg-[#ff4500] text-white border-[#ff4500]" : step > n ? "bg-[#1a8754] text-white border-[#1a8754]" : "bg-white text-[#1a1a1a]/50 border-[#1a1a1a]/15"}`}>
              {step > n ? "✓" : n}. {["Preview", "Confirm scope", "Download", "Confirm receipt", "Delete"][n-1]}
            </div>
          ))}
        </div>
      </div>

      <StorageWidget />

      {step === 1 && (
        <div className="surface-card rounded-2xl p-5 space-y-3" data-testid="vault-step-1">
          <div>
            <div className="font-display font-bold text-base">Step 1 — Pick what to archive</div>
            <div className="text-[12px] text-[#1a1a1a]/60">Only closed records older than {preview.min_age_days} days are eligible. Recent items ({preview.blocked_recent_count} total) are blocked to protect active traceability.</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {preview.collections.map((c) => {
              const on = selectedKeys.includes(c.key);
              const disabled = c.count === 0;
              return (
                <button key={c.key} type="button" disabled={disabled}
                  onClick={() => setSelectedKeys(on ? selectedKeys.filter((k) => k !== c.key) : [...selectedKeys, c.key])}
                  data-testid={`vault-coll-${c.key}`}
                  className={`text-left p-3 rounded-xl border transition-all ${on ? "bg-[#ff4500]/10 border-[#ff4500]" : disabled ? "bg-[#f7f5f2] border-[#1a1a1a]/08 opacity-50 cursor-not-allowed" : "bg-white border-[#1a1a1a]/15"}`}>
                  <div className="font-display font-bold text-sm">{c.label}</div>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-[#1a1a1a]/55">{c.count} eligible</div>
                </button>
              );
            })}
          </div>
          <div className="flex justify-end">
            <button onClick={() => setStep(2)} disabled={selectedKeys.length === 0 || totalSelected === 0}
              data-testid="vault-step-1-next"
              className="btn-pill btn-pill-primary disabled:opacity-40">Next — {totalSelected} items →</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="surface-card rounded-2xl p-5 space-y-3" data-testid="vault-step-2">
          <div>
            <div className="font-display font-bold text-base">Step 2 — Confirm scope</div>
            <div className="text-[12px] text-[#1a1a1a]/60">You&apos;re about to archive <strong>{totalSelected}</strong> records across {selectedKeys.length} collection(s). Type the confirmation phrase to continue.</div>
          </div>
          <div className="bg-[#fff1ea] border border-[#ff4500]/30 rounded-xl p-3 text-[13px] text-[#1a1a1a]">
            Type this exactly: <span className="font-mono font-bold text-[#ff4500]">ARCHIVE AND REMOVE</span>
          </div>
          <input value={phrase1} onChange={(e) => setPhrase1(e.target.value)}
            data-testid="vault-phrase-1"
            placeholder="ARCHIVE AND REMOVE"
            className="w-full bg-white border border-[#1a1a1a]/20 rounded-xl px-3 py-2 text-sm font-mono" />
          <div className="flex justify-between">
            <button onClick={() => setStep(1)} className="btn-pill btn-pill-ghost">← Back</button>
            <button onClick={prepareBundle} disabled={busy || phrase1 !== "ARCHIVE AND REMOVE"}
              data-testid="vault-step-2-prepare"
              className="btn-pill btn-pill-primary disabled:opacity-40">
              <ShieldAlert className="w-3.5 h-3.5" /> {busy ? "Building bundle…" : "Prepare download bundle"}
            </button>
          </div>
        </div>
      )}

      {step === 3 && bundle && (
        <div className="surface-card rounded-2xl p-5 space-y-3" data-testid="vault-step-3">
          <div>
            <div className="font-display font-bold text-base">Step 3 — Download the archive</div>
            <div className="text-[12px] text-[#1a1a1a]/60">Download the ZIP and store it somewhere safe (your local drive, SharePoint, etc.). It contains one JSON per collection + manifest.csv.</div>
          </div>
          <div className="bg-[#f7f5f2] rounded-xl p-3 text-[12px]">
            <div><strong>Items:</strong> {bundle.item_count}</div>
            <div><strong>Size:</strong> {(bundle.byte_size / 1024).toFixed(1)} KB</div>
            <div><strong>SHA-256:</strong> <span className="font-mono text-[10px]">{bundle.sha256}</span></div>
            <div><strong>Expires:</strong> {new Date(bundle.expires_at).toLocaleString()}</div>
          </div>
          <button onClick={doDownload} data-testid="vault-download-btn"
            className="btn-pill btn-pill-primary"><Download className="w-3.5 h-3.5" /> {downloaded ? "Re-download" : "Download bundle"}</button>
          <div className="flex justify-between">
            <button onClick={() => { setStep(2); setBundle(null); }} className="btn-pill btn-pill-ghost">← Back</button>
            <button onClick={() => setStep(4)} disabled={!downloaded}
              data-testid="vault-step-3-next"
              className="btn-pill btn-pill-primary disabled:opacity-40">Next — I saved it →</button>
          </div>
        </div>
      )}

      {step === 4 && bundle && (
        <div className="surface-card rounded-2xl p-5 space-y-3" data-testid="vault-step-4">
          <div>
            <div className="font-display font-bold text-base">Step 4 — Confirm you saved the archive</div>
            <div className="text-[12px] text-[#1a1a1a]/60">This creates a permanent audit-log entry that you have received and stored the archive. Without this, delete is blocked.</div>
          </div>
          <button onClick={confirmDownload} disabled={!downloaded || downloadConfirmed}
            data-testid="vault-confirm-download"
            className="btn-pill btn-pill-primary disabled:opacity-40">
            <CheckCircle2 className="w-3.5 h-3.5" /> {downloadConfirmed ? "Confirmed" : "I have safely stored this archive"}
          </button>
          <div className="flex justify-between">
            <button onClick={() => setStep(3)} className="btn-pill btn-pill-ghost">← Back</button>
            <button onClick={() => setStep(5)} disabled={!downloadConfirmed}
              data-testid="vault-step-4-next"
              className="btn-pill btn-pill-primary disabled:opacity-40">Next — final delete →</button>
          </div>
        </div>
      )}

      {step === 5 && bundle && (
        <div className="surface-card rounded-2xl p-5 space-y-3" data-testid="vault-step-5">
          <div>
            <div className="font-display font-bold text-base text-[#ff4500]">Step 5 — Permanent delete</div>
            <div className="text-[12px] text-[#1a1a1a]/60">Type the final confirmation phrase. This action cannot be undone.</div>
          </div>
          <div className="bg-[#fff1ea] border-2 border-[#ff4500]/40 rounded-xl p-3 text-[13px]">
            Type this exactly: <span className="font-mono font-bold text-[#ff4500]">PERMANENTLY DELETE {bundle.item_count} ITEMS</span>
          </div>
          <input value={phrase2} onChange={(e) => setPhrase2(e.target.value)}
            data-testid="vault-phrase-2"
            placeholder={`PERMANENTLY DELETE ${bundle.item_count} ITEMS`}
            className="w-full bg-white border border-[#ff4500]/40 rounded-xl px-3 py-2 text-sm font-mono" />
          <div className="flex justify-between">
            <button onClick={() => setStep(4)} className="btn-pill btn-pill-ghost">← Back</button>
            <button onClick={executeDelete} disabled={busy || phrase2 !== `PERMANENTLY DELETE ${bundle.item_count} ITEMS`}
              data-testid="vault-step-5-execute"
              className="btn-pill btn-pill-primary bg-[#ff4500] disabled:opacity-40">
              <Trash2 className="w-3.5 h-3.5" /> {busy ? "Deleting…" : "Permanently delete"}
            </button>
          </div>
        </div>
      )}

      <div className="surface-card rounded-2xl p-5" data-testid="vault-audit-log">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1a1a1a]/55">// Immutable audit log — never deletable</div>
        <div className="font-display font-bold text-base mb-2">Recent activity ({log.length})</div>
        {log.length === 0 ? (
          <div className="text-[12px] text-[#1a1a1a]/55">No archive activity yet.</div>
        ) : (
          <ul className="divide-y divide-[#1a1a1a]/06 max-h-64 overflow-auto scrollbar-thin">
            {log.map((entry) => (
              <li key={entry.id} className="py-2 text-[12px]">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[9px] font-mono uppercase tracking-wider font-bold text-[#ff4500]">{entry.kind}</span>
                  <span className="text-[9px] font-mono text-[#1a1a1a]/50">{new Date(entry.created_at).toLocaleString()}</span>
                  <span className="text-[9px] font-mono text-[#1a1a1a]/55 ml-auto">— {entry.user_name}</span>
                </div>
                <div className="text-[11px] text-[#1a1a1a]">{entry.text}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


// iter37 — Storage dashboard widget inside Data Vault tab
function StorageWidget() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/data-vault/storage-stats").then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);
  const fmt = (n) => {
    if (!n) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB"]; let i = 0; let x = n;
    while (x >= 1024 && i < units.length - 1) { x /= 1024; i++; }
    return `${x.toFixed(x >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
  };
  if (!stats) return null;
  return (
    <div className="surface-card rounded-2xl p-5 space-y-3" data-testid="storage-widget">
      <div className="flex items-center gap-2 text-[#ff4500]">
        <Archive className="w-4 h-4" />
        <div className="font-mono text-[10px] uppercase tracking-[0.2em]">// Live storage dashboard</div>
      </div>
      <div className="font-display font-black text-xl">Storage & Compression Savings</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["Files", stats.file_count, ""],
          ["Stored", fmt(stats.total_size), "current"],
          ["Original", fmt(stats.total_original), "before compression"],
          ["Savings", `${stats.savings_pct}%`, fmt(stats.savings)],
        ].map(([l, v, sub]) => (
          <div key={l} className="p-3 rounded-lg bg-[#f7f5f2] border border-[#1a1a1a]/06">
            <div className="text-[9px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">{l}</div>
            <div className="font-display font-black text-lg text-[#1a1a1a]">{v}</div>
            {sub && <div className="text-[9px] font-mono uppercase tracking-wider text-[#1a1a1a]/40">{sub}</div>}
          </div>
        ))}
      </div>
      {stats.by_type.length > 0 && (
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60 font-bold mb-1">By type</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[9px] uppercase tracking-wider text-[#1a1a1a]/55">
                <th className="pb-1">Bucket</th>
                <th className="pb-1">Files</th>
                <th className="pb-1">Stored</th>
                <th className="pb-1">Original</th>
                <th className="pb-1">Savings</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1a1a1a]/06">
              {stats.by_type.map((b) => (
                <tr key={b.bucket} data-testid={`storage-row-${b.bucket}`}>
                  <td className="py-1 font-display font-bold text-[13px]">{b.bucket}</td>
                  <td className="py-1 text-[12px]">{b.count}</td>
                  <td className="py-1 text-[12px]">{fmt(b.size)}</td>
                  <td className="py-1 text-[12px]">{fmt(b.original)}</td>
                  <td className="py-1 text-[12px] text-[#1a8754] font-bold">{fmt(b.savings)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">
        {stats.vault_eligible_records} records eligible for archive (older than 90 days)
      </div>
    </div>
  );
}


// iter51 — Small reusable editors for Doc Library vocab.
function ListEditor({ label, items, onChange, placeholder, testId }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (items.includes(v)) return toast.error("Already in list");
    onChange([...items, v]);
    setDraft("");
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="mb-4" data-testid={testId}>
      <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/65 mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {items.map((it, i) => (
          <span key={`${it}-${i}`} data-testid={`${testId}-item-${i}`} className="inline-flex items-center gap-1 bg-[#f7f5f2] border border-[#1a1a1a]/10 rounded-full pl-3 pr-1 py-0.5 text-[11px]">
            {it}
            <button onClick={() => remove(i)} className="w-4 h-4 rounded-full text-[#1a1a1a]/50 hover:text-[#ff4500] hover:bg-[#1a1a1a]/06 flex items-center justify-center" title="Remove">×</button>
          </span>
        ))}
        {items.length === 0 && <span className="text-[11px] text-[#1a1a1a]/40 italic">No items — using defaults on next save.</span>}
      </div>
      <div className="flex gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder={placeholder} className="flex-1 border border-[#1a1a1a]/15 rounded-lg px-3 py-1.5 text-[12px]"
          data-testid={`${testId}-input`} />
        <button onClick={add} data-testid={`${testId}-add`} className="btn-pill btn-pill-secondary text-[11px]"><Plus className="w-3 h-3" /> Add</button>
      </div>
    </div>
  );
}

function PrefixEditor({ items, onChange }) {
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const add = () => {
    const c = code.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
    const l = label.trim();
    if (!c) return toast.error("Prefix code required (letters/numbers only)");
    if (items.some((x) => x.code === c)) return toast.error("Prefix already exists");
    onChange([...items, { code: c, label: l || c }]);
    setCode(""); setLabel("");
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="mb-4" data-testid="prefixes-editor">
      <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/65 mb-1.5">Code Prefixes (used to generate Doc IDs like SOP-0001)</div>
      <div className="space-y-1 mb-2">
        {items.map((p, i) => (
          <div key={`${p.code}-${i}`} data-testid={`prefixes-editor-item-${i}`}
            className="flex items-center justify-between bg-[#f7f5f2] border border-[#1a1a1a]/08 rounded-lg px-3 py-1.5">
            <div className="flex items-center gap-3">
              <span className="font-mono font-bold text-[#ff4500] text-[12px] w-14">{p.code}</span>
              <span className="text-[12px] text-[#1a1a1a]/80">{p.label}</span>
            </div>
            <button onClick={() => remove(i)} className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/50 hover:text-[#ff4500]" title="Remove">Remove</button>
          </div>
        ))}
        {items.length === 0 && <div className="text-[11px] text-[#1a1a1a]/40 italic">No prefixes — using defaults on next save.</div>}
      </div>
      <div className="grid grid-cols-[100px_1fr_auto] gap-2">
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="SOP"
          className="border border-[#1a1a1a]/15 rounded-lg px-2 py-1.5 text-[12px] font-mono font-bold" maxLength={6}
          data-testid="prefixes-editor-code" />
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Description (optional)"
          className="border border-[#1a1a1a]/15 rounded-lg px-3 py-1.5 text-[12px]"
          data-testid="prefixes-editor-label" />
        <button onClick={add} data-testid="prefixes-editor-add" className="btn-pill btn-pill-secondary text-[11px]"><Plus className="w-3 h-3" /> Add</button>
      </div>
    </div>
  );
}

